import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Save, Plus, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import AdminLayout from "./AdminLayout";
import FileUpload from "../../components/FileUpload";

const EMPTY_SECTIONS = {
    hero: { title: "", subtitle: "", hooks: [] },
    why_this_matters: { content: "", who_affected: "", why_now: "" },
    official_position: { label: "Mainstream / Government Position", content: "" },
    system_breakdown: { who_builds: "", who_funds: "", who_benefits: "", how_it_works: "" },
    critical_questions: [],
    evidence_sections: { documented: [], reported: [], debated: [] },
    supporting_view: { content: "" },
    critical_view: { content: "" },
    real_world_examples: [],
    unknowns: [],
    future_outlook: { content: "" },
    key_takeaways: [],
    next_topics: [],
};

const FUNDING_OPTS = ["independent", "industry", "government", "unknown"];

function Field({ label, hint, children, required }) {
    return (
        <div>
            <label className="block text-xs uppercase tracking-wider text-[#94A3B8] mb-2">
                {label} {required && <span className="text-red-400">*</span>}
            </label>
            {children}
            {hint && <p className="text-xs text-[#64748B] mt-1">{hint}</p>}
        </div>
    );
}

const inputCls = "w-full bg-[#0B1220] border border-[#243044] rounded-lg px-4 py-2.5 text-sm text-white outline-none focus:border-[#2F80ED] transition-colors placeholder-[#64748B]";

export default function AdminTopicEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);

    const [topic, setTopic] = useState({
        slug: "",
        title: "",
        subtitle: "",
        category: "",
        tags: "",
        summary: "",
        overview: "",
        confidence_score: 0,
        concern_level: 0,
        evidence_level: "medium",
        related_topics: "",
        priority: 0,
        status: "draft",
        topic_layer: "sub_investigation",
        parent_topic_slug: "",
        author_name: "Bill Mathers",
        author_role: "The Context Project",
        compiled_by: "Compiled by Bill Mathers",
        seo_title: "",
        meta_description: "",
        issue_report_url: "/pages/report-an-issue",
        featured_image: "",
        entities: [],
        timeline: [],
        sections: EMPTY_SECTIONS,
        sources: [],
    });

    useEffect(() => {
        if (!isEdit) return;
        api.get(`/admin/topics/${id}`)
            .then((r) => {
                const d = r.data;
                setTopic({
                    ...d,
                    tags: Array.isArray(d.tags) ? d.tags.join(", ") : (d.tags || ""),
                    related_topics: Array.isArray(d.related_topics) ? d.related_topics.join(", ") : (d.related_topics || ""),
                    sections: { ...EMPTY_SECTIONS, ...(d.sections || {}) },
                    sources: d.sources || [],
                    entities: d.entities || [],
                    timeline: d.timeline || [],
                    featured_image: d.featured_image || "",
                });
            })
            .catch(() => toast.error("Failed to load topic"))
            .finally(() => setLoading(false));
    }, [id, isEdit]);

    const update = (key, value) => setTopic((t) => ({ ...t, [key]: value }));
    const updateSection = (sec, key, value) => setTopic((t) => ({
        ...t,
        sections: { ...t.sections, [sec]: { ...(t.sections[sec] || {}), [key]: value } },
    }));
    const updateSectionList = (sec, value) => setTopic((t) => ({
        ...t,
        sections: { ...t.sections, [sec]: value },
    }));

    const onSave = async () => {
        if (!topic.title.trim() || !topic.slug.trim()) {
            toast.error("Title and slug are required");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...topic,
                tags: topic.tags.split(",").map((s) => s.trim()).filter(Boolean),
                related_topics: typeof topic.related_topics === "string"
                    ? topic.related_topics.split(",").map((s) => s.trim()).filter(Boolean)
                    : topic.related_topics,
                confidence_score: Number(topic.confidence_score) || 0,
                concern_level: Number(topic.concern_level) || 0,
                priority: Number(topic.priority) || 0,
            };
            if (isEdit) {
                await api.put(`/admin/topics/${id}`, payload);
                toast.success("Topic saved");
            } else {
                const { data } = await api.post("/admin/topics", payload);
                toast.success("Topic created");
                navigate(`/admin/topics/${data.id}/edit`, { replace: true });
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const addSource = () => {
        const newSource = { id: crypto.randomUUID(), title: "", url: "", publisher: "", source_type: "", funding: "unknown", date: "", notes: "" };
        update("sources", [...topic.sources, newSource]);
    };
    const updateSource = (idx, key, value) => {
        const next = [...topic.sources];
        next[idx] = { ...next[idx], [key]: value };
        update("sources", next);
    };
    const removeSource = (idx) => {
        const next = topic.sources.filter((_, i) => i !== idx);
        update("sources", next);
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="p-12 text-center text-[#64748B]">Loading topic…</div>
            </AdminLayout>
        );
    }

    const s = topic.sections || EMPTY_SECTIONS;

    return (
        <AdminLayout>
            <div className="sticky top-0 z-10 bg-[#0B1220]/95 backdrop-blur border-b border-[#243044]">
                <div className="px-8 py-4 flex items-center justify-between gap-4 flex-wrap">
                    <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-[#64748B] hover:text-[#56CCF2] uppercase tracking-wider">
                        <ChevronLeft className="w-3 h-3" />
                        Dashboard
                    </Link>
                    <div className="flex items-center gap-3">
                        <select
                            value={topic.status}
                            onChange={(e) => update("status", e.target.value)}
                            data-testid="topic-status"
                            className="bg-[#111827] border border-[#243044] text-white text-xs uppercase tracking-wider px-3 py-2 rounded-lg outline-none focus:border-[#2F80ED]"
                        >
                            <option value="draft">Draft</option>
                            <option value="published">Published</option>
                            <option value="archived">Archived</option>
                            <option value="hidden_internal">Hidden / Internal</option>
                        </select>
                        <button
                            data-testid="topic-save"
                            onClick={onSave}
                            disabled={saving}
                            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#2F80ED] hover:bg-[#2F80ED]/90 text-white text-sm font-medium transition-colors disabled:opacity-50"
                        >
                            <Save className="w-4 h-4" />
                            {saving ? "Saving…" : "Save"}
                        </button>
                    </div>
                </div>
            </div>

            <div className="p-8 sm:p-10 max-w-5xl space-y-10">
                <h1 className="text-3xl font-bold text-white">{isEdit ? "Edit topic" : "New topic"}</h1>

                {/* CORE */}
                <section className="space-y-5">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">Core</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <Field label="Title" required>
                            <input data-testid="field-title" className={inputCls} value={topic.title} onChange={(e) => update("title", e.target.value)} />
                        </Field>
                        <Field label="Slug" required hint="URL identifier, lowercase-hyphenated">
                            <input data-testid="field-slug" className={inputCls} value={topic.slug} onChange={(e) => update("slug", e.target.value)} />
                        </Field>
                        <Field label="Subtitle">
                            <input className={inputCls} value={topic.subtitle} onChange={(e) => update("subtitle", e.target.value)} />
                        </Field>
                        <Field label="Category">
                            <input className={inputCls} value={topic.category} onChange={(e) => update("category", e.target.value)} />
                        </Field>
                        <Field label="Tags" hint="Comma-separated">
                            <input className={inputCls} value={topic.tags} onChange={(e) => update("tags", e.target.value)} />
                        </Field>
                        <Field label="Related topics" hint="Comma-separated slugs">
                            <input className={inputCls} value={topic.related_topics} onChange={(e) => update("related_topics", e.target.value)} />
                        </Field>
                        <Field label="Summary" hint="One-line summary">
                            <textarea rows={2} className={inputCls} value={topic.summary} onChange={(e) => update("summary", e.target.value)} />
                        </Field>
                        <Field label="Overview" hint="Short paragraph">
                            <textarea rows={2} className={inputCls} value={topic.overview} onChange={(e) => update("overview", e.target.value)} />
                        </Field>
                        <Field label="Topic layer" hint="3-layer architecture (Task D)">
                            <select
                                data-testid="field-topic-layer"
                                className={inputCls}
                                value={topic.topic_layer || "sub_investigation"}
                                onChange={(e) => update("topic_layer", e.target.value)}
                            >
                                <option value="foundational">Foundational system</option>
                                <option value="sub_investigation">Sub-investigation</option>
                                <option value="entity_event">Entity / event</option>
                            </select>
                        </Field>
                        <Field label="Parent topic slug" hint="Foundational topic this nests under (sub_investigations only)">
                            <input
                                data-testid="field-parent-topic-slug"
                                className={inputCls}
                                value={topic.parent_topic_slug || ""}
                                onChange={(e) => update("parent_topic_slug", e.target.value)}
                                placeholder="e.g. food-supply-systems"
                            />
                        </Field>
                    </div>
                </section>

                {/* SCORING */}
                <section className="space-y-5">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">Scoring</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <Field label="Confidence (0-100)">
                            <input type="number" min="0" max="100" className={inputCls} value={topic.confidence_score} onChange={(e) => update("confidence_score", e.target.value)} />
                        </Field>
                        <Field label="Concern (0-100)">
                            <input type="number" min="0" max="100" className={inputCls} value={topic.concern_level} onChange={(e) => update("concern_level", e.target.value)} />
                        </Field>
                        <Field label="Evidence level">
                            <select className={inputCls} value={topic.evidence_level} onChange={(e) => update("evidence_level", e.target.value)}>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                            </select>
                        </Field>
                        <Field label="Priority">
                            <input type="number" className={inputCls} value={topic.priority} onChange={(e) => update("priority", e.target.value)} />
                        </Field>
                    </div>
                </section>

                {/* SEO */}
                <section className="space-y-5">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">SEO</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <Field label="SEO title">
                            <input className={inputCls} value={topic.seo_title} onChange={(e) => update("seo_title", e.target.value)} />
                        </Field>
                        <Field label="Issue report URL">
                            <input className={inputCls} value={topic.issue_report_url} onChange={(e) => update("issue_report_url", e.target.value)} />
                        </Field>
                        <div className="md:col-span-2">
                            <Field label="Meta description">
                                <textarea rows={2} className={inputCls} value={topic.meta_description} onChange={(e) => update("meta_description", e.target.value)} />
                            </Field>
                        </div>
                    </div>
                </section>

                {/* FEATURED IMAGE */}
                <section className="space-y-5">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">Featured image</h2>
                    <FileUpload
                        value={topic.featured_image}
                        onChange={(url) => update("featured_image", url || "")}
                        accept="image/*"
                        label="Upload featured image"
                        kind="image"
                    />
                </section>

                {/* ENTITIES */}
                <section className="space-y-5">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">Entities (people / companies / organisations)</h2>
                        <button
                            type="button"
                            data-testid="add-entity"
                            onClick={() => update("entities", [...(topic.entities || []), { id: crypto.randomUUID(), name: "", type: "company", role: "", bio: "", url: "" }])}
                            className="inline-flex items-center gap-1.5 text-xs text-[#56CCF2] hover:text-white"
                        >
                            <Plus className="w-3.5 h-3.5" /> Add entity
                        </button>
                    </div>
                    {(topic.entities || []).length === 0 ? (
                        <p className="text-sm text-[#64748B]">No entities yet. Add the key people, companies, or organisations relevant to this investigation.</p>
                    ) : (
                        <div className="space-y-3">
                            {(topic.entities || []).map((ent, idx) => (
                                <div key={ent.id || idx} className="bg-[#0B1220] border border-[#243044] rounded-xl p-4 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-[#64748B]">Entity #{idx + 1}</span>
                                        <button
                                            type="button"
                                            onClick={() => update("entities", topic.entities.filter((_, i) => i !== idx))}
                                            className="text-[#94A3B8] hover:text-red-400"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <input className={inputCls} placeholder="Name" value={ent.name || ""} onChange={(e) => {
                                            const next = [...topic.entities]; next[idx] = { ...next[idx], name: e.target.value }; update("entities", next);
                                        }} />
                                        <select className={inputCls} value={ent.type || "company"} onChange={(e) => {
                                            const next = [...topic.entities]; next[idx] = { ...next[idx], type: e.target.value }; update("entities", next);
                                        }}>
                                            <option value="person">Person</option>
                                            <option value="company">Company</option>
                                            <option value="organization">Organization</option>
                                        </select>
                                        <input className={inputCls} placeholder="Role / position" value={ent.role || ""} onChange={(e) => {
                                            const next = [...topic.entities]; next[idx] = { ...next[idx], role: e.target.value }; update("entities", next);
                                        }} />
                                        <input className={inputCls} placeholder="Reference URL" value={ent.url || ""} onChange={(e) => {
                                            const next = [...topic.entities]; next[idx] = { ...next[idx], url: e.target.value }; update("entities", next);
                                        }} />
                                        <textarea rows={2} className={`${inputCls} md:col-span-2`} placeholder="Short bio / description" value={ent.bio || ""} onChange={(e) => {
                                            const next = [...topic.entities]; next[idx] = { ...next[idx], bio: e.target.value }; update("entities", next);
                                        }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* TIMELINE */}
                <section className="space-y-5">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">Documented timeline</h2>
                        <button
                            type="button"
                            data-testid="add-timeline"
                            onClick={() => update("timeline", [...(topic.timeline || []), { id: crypto.randomUUID(), date: "", title: "", description: "" }])}
                            className="inline-flex items-center gap-1.5 text-xs text-[#56CCF2] hover:text-white"
                        >
                            <Plus className="w-3.5 h-3.5" /> Add timeline event
                        </button>
                    </div>
                    {(topic.timeline || []).length === 0 ? (
                        <p className="text-sm text-[#64748B]">No timeline yet. Add documented historical events relevant to this topic.</p>
                    ) : (
                        <div className="space-y-3">
                            {(topic.timeline || []).map((ev, idx) => (
                                <div key={ev.id || idx} className="bg-[#0B1220] border border-[#243044] rounded-xl p-4 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-[#64748B]">Event #{idx + 1}</span>
                                        <button
                                            type="button"
                                            onClick={() => update("timeline", topic.timeline.filter((_, i) => i !== idx))}
                                            className="text-[#94A3B8] hover:text-red-400"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <input className={inputCls} placeholder="Date (e.g., 1947 or 1947-10-13)" value={ev.date || ""} onChange={(e) => {
                                            const next = [...topic.timeline]; next[idx] = { ...next[idx], date: e.target.value }; update("timeline", next);
                                        }} />
                                        <input className={`${inputCls} md:col-span-2`} placeholder="Event title" value={ev.title || ""} onChange={(e) => {
                                            const next = [...topic.timeline]; next[idx] = { ...next[idx], title: e.target.value }; update("timeline", next);
                                        }} />
                                        <textarea rows={2} className={`${inputCls} md:col-span-3`} placeholder="Description" value={ev.description || ""} onChange={(e) => {
                                            const next = [...topic.timeline]; next[idx] = { ...next[idx], description: e.target.value }; update("timeline", next);
                                        }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* HERO */}
                <section className="space-y-5">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">1 / Hero</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <Field label="Hero title (defaults to title)">
                            <input className={inputCls} value={s.hero?.title || ""} onChange={(e) => updateSection("hero", "title", e.target.value)} />
                        </Field>
                        <Field label="Hero subtitle">
                            <input className={inputCls} value={s.hero?.subtitle || ""} onChange={(e) => updateSection("hero", "subtitle", e.target.value)} />
                        </Field>
                        <div className="md:col-span-2">
                            <Field label="3 hooks (one per line)">
                                <textarea
                                    rows={3}
                                    className={inputCls}
                                    value={(s.hero?.hooks || []).join("\n")}
                                    onChange={(e) => updateSection("hero", "hooks", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))}
                                />
                            </Field>
                        </div>
                    </div>
                </section>

                {/* WHY THIS MATTERS */}
                <section className="space-y-5">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">2 / Why this matters</h2>
                    <Field label="Content">
                        <textarea rows={3} className={inputCls} value={s.why_this_matters?.content || ""} onChange={(e) => updateSection("why_this_matters", "content", e.target.value)} />
                    </Field>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <Field label="Who is affected">
                            <textarea rows={2} className={inputCls} value={s.why_this_matters?.who_affected || ""} onChange={(e) => updateSection("why_this_matters", "who_affected", e.target.value)} />
                        </Field>
                        <Field label="Why now">
                            <textarea rows={2} className={inputCls} value={s.why_this_matters?.why_now || ""} onChange={(e) => updateSection("why_this_matters", "why_now", e.target.value)} />
                        </Field>
                    </div>
                </section>

                {/* OFFICIAL POSITION */}
                <section className="space-y-5">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">3 / Official position</h2>
                    <Field label="Label">
                        <input className={inputCls} value={s.official_position?.label || ""} onChange={(e) => updateSection("official_position", "label", e.target.value)} />
                    </Field>
                    <Field label="Content">
                        <textarea rows={3} className={inputCls} value={s.official_position?.content || ""} onChange={(e) => updateSection("official_position", "content", e.target.value)} />
                    </Field>
                </section>

                {/* SYSTEM BREAKDOWN */}
                <section className="space-y-5">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">4 / System breakdown</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {["who_builds", "who_funds", "who_benefits", "how_it_works"].map((k) => (
                            <Field key={k} label={k.replace(/_/g, " ")}>
                                <textarea rows={2} className={inputCls} value={s.system_breakdown?.[k] || ""} onChange={(e) => updateSection("system_breakdown", k, e.target.value)} />
                            </Field>
                        ))}
                    </div>
                </section>

                {/* CRITICAL QUESTIONS */}
                <section className="space-y-5">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">5 / Critical questions</h2>
                    <Field label="Questions (one per line)">
                        <textarea
                            rows={5}
                            className={inputCls}
                            value={(s.critical_questions || []).join("\n")}
                            onChange={(e) => updateSectionList("critical_questions", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))}
                        />
                    </Field>
                </section>

                {/* EVIDENCE */}
                <section className="space-y-5">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">6 / Evidence (JSON-style entries)</h2>
                    <p className="text-xs text-[#64748B]">Format per tier: one entry per line as <code className="text-[#56CCF2]">title :: content</code></p>
                    {["documented", "reported", "debated"].map((tier) => {
                        const arr = (s.evidence_sections?.[tier] || []);
                        const text = arr.map((it) => `${it.title || ""} :: ${it.content || ""}`).join("\n");
                        return (
                            <Field key={tier} label={tier}>
                                <textarea
                                    rows={4}
                                    className={inputCls}
                                    value={text}
                                    onChange={(e) => {
                                        const items = e.target.value.split("\n").filter((x) => x.trim()).map((line) => {
                                            const [title, ...rest] = line.split("::");
                                            return { title: (title || "").trim(), content: rest.join("::").trim() };
                                        });
                                        updateSection("evidence_sections", tier, items);
                                    }}
                                />
                            </Field>
                        );
                    })}
                </section>

                {/* BOTH SIDES */}
                <section className="space-y-5">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">7 / Both sides</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <Field label="Supporting view">
                            <textarea rows={4} className={inputCls} value={s.supporting_view?.content || ""} onChange={(e) => updateSection("supporting_view", "content", e.target.value)} />
                        </Field>
                        <Field label="Critical view">
                            <textarea rows={4} className={inputCls} value={s.critical_view?.content || ""} onChange={(e) => updateSection("critical_view", "content", e.target.value)} />
                        </Field>
                    </div>
                </section>

                {/* REAL WORLD EXAMPLES */}
                <section className="space-y-5">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">9 / Real-world examples</h2>
                    <Field label="Examples (one per line: title :: content)">
                        <textarea
                            rows={4}
                            className={inputCls}
                            value={(s.real_world_examples || []).map((it) => `${it.title || ""} :: ${it.content || ""}`).join("\n")}
                            onChange={(e) => {
                                const items = e.target.value.split("\n").filter((x) => x.trim()).map((line) => {
                                    const [title, ...rest] = line.split("::");
                                    return { title: (title || "").trim(), content: rest.join("::").trim() };
                                });
                                updateSectionList("real_world_examples", items);
                            }}
                        />
                    </Field>
                </section>

                {/* UNKNOWNS */}
                <section className="space-y-5">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">10 / What's unclear</h2>
                    <Field label="Unknowns (one per line)">
                        <textarea
                            rows={4}
                            className={inputCls}
                            value={(s.unknowns || []).join("\n")}
                            onChange={(e) => updateSectionList("unknowns", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))}
                        />
                    </Field>
                </section>

                {/* FUTURE */}
                <section className="space-y-5">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">11 / Where this could go</h2>
                    <Field label="Content">
                        <textarea rows={3} className={inputCls} value={s.future_outlook?.content || ""} onChange={(e) => updateSection("future_outlook", "content", e.target.value)} />
                    </Field>
                </section>

                {/* TAKEAWAYS */}
                <section className="space-y-5">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">12 / Key takeaways</h2>
                    <Field label="Takeaways (one per line)">
                        <textarea
                            rows={4}
                            className={inputCls}
                            value={(s.key_takeaways || []).join("\n")}
                            onChange={(e) => updateSectionList("key_takeaways", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))}
                        />
                    </Field>
                </section>

                {/* NEXT TOPICS */}
                <section className="space-y-5">
                    <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">13 / Next topics</h2>
                    <Field label="Slugs (one per line)">
                        <textarea
                            rows={3}
                            className={inputCls}
                            value={(s.next_topics || []).join("\n")}
                            onChange={(e) => updateSectionList("next_topics", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))}
                        />
                    </Field>
                </section>

                {/* SOURCES */}
                <section className="space-y-5">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xs uppercase tracking-[0.2em] text-[#56CCF2] font-semibold">Sources</h2>
                        <button
                            data-testid="add-source"
                            onClick={addSource}
                            type="button"
                            className="inline-flex items-center gap-1.5 text-xs text-[#56CCF2] hover:text-white transition-colors"
                        >
                            <Plus className="w-3.5 h-3.5" /> Add source
                        </button>
                    </div>
                    {(topic.sources || []).length === 0 ? (
                        <p className="text-sm text-[#64748B]">No sources yet. Add at least 5–10 high-quality sources.</p>
                    ) : (
                        <div className="space-y-3">
                            {(topic.sources || []).map((src, idx) => (
                                <div key={src.id || idx} className="bg-[#0B1220] border border-[#243044] rounded-xl p-4 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-[#64748B]">Source #{idx + 1}</span>
                                        <button onClick={() => removeSource(idx)} type="button" className="text-[#94A3B8] hover:text-red-400">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <input className={inputCls} placeholder="Title" value={src.title || ""} onChange={(e) => updateSource(idx, "title", e.target.value)} />
                                        <input className={inputCls} placeholder="URL" value={src.url || ""} onChange={(e) => updateSource(idx, "url", e.target.value)} />
                                        <input className={inputCls} placeholder="Publisher" value={src.publisher || ""} onChange={(e) => updateSource(idx, "publisher", e.target.value)} />
                                        <input className={inputCls} placeholder="Source type (journal/gov/regulatory/news/patent)" value={src.source_type || ""} onChange={(e) => updateSource(idx, "source_type", e.target.value)} />
                                        <select className={inputCls} value={src.funding || "unknown"} onChange={(e) => updateSource(idx, "funding", e.target.value)}>
                                            {FUNDING_OPTS.map((f) => <option key={f} value={f}>{f}</option>)}
                                        </select>
                                        <input className={inputCls} placeholder="Date (YYYY-MM-DD)" value={src.date || ""} onChange={(e) => updateSource(idx, "date", e.target.value)} />
                                        <input className={`${inputCls} md:col-span-2`} placeholder="Document URL (optional, PDF link)" value={src.document_url || ""} onChange={(e) => updateSource(idx, "document_url", e.target.value)} />
                                        <textarea rows={2} className={`${inputCls} md:col-span-2`} placeholder="Notes" value={src.notes || ""} onChange={(e) => updateSource(idx, "notes", e.target.value)} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </AdminLayout>
    );
}
